import { type ReactElement } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { EnforcementSeverityThresholdControl } from '../EnforcementSeverityThresholdControl';
import {
  SecretConfidenceThresholdControl,
  SecretConfidenceInfoTooltip,
} from '../SecretConfidenceThresholdControl';
import { usePersonalEnforcementSeverity } from './usePersonalEnforcementSeverity';
import { usePersonalSecretConfidence } from './usePersonalSecretConfidence';

type Props = {
  isEnforcePolicyModeEnabled: boolean;
  isSecretConfidenceThresholdEnabled: boolean;
};

function LoadError({ setting }: { setting: string }): ReactElement {
  return (
    <div className="rounded-md bg-destructive/15 p-4">
      <p className="text-sm text-destructive font-medium">
        Failed to load {setting}
      </p>
      <p className="text-sm text-muted-foreground mt-1">
        Please retry or contact support if the issue persists.
      </p>
    </div>
  );
}

function EnforcementCard(): ReactElement {
  const {
    severity,
    isLoading,
    hasError,
    isSaving,
    hasAnyEnforcePolicy,
    onChange,
  } = usePersonalEnforcementSeverity(true);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Enforcement Severity Threshold</CardTitle>
        <CardDescription>
          When policies are set to Enforce, this threshold determines the
          minimum severity at which protective actions are applied
        </CardDescription>
      </CardHeader>
      <CardContent>
        {hasError ? (
          <LoadError setting="enforcement severity threshold" />
        ) : (
          <EnforcementSeverityThresholdControl
            value={severity}
            onChange={onChange}
            disabled={isLoading || !hasAnyEnforcePolicy}
            saving={isSaving}
          />
        )}
      </CardContent>
    </Card>
  );
}

function ConfidenceCard(): ReactElement {
  const { confidence, isLoading, hasError, isSaving, onChange } =
    usePersonalSecretConfidence(true);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-1.5">
          Secret Alert Confidence Threshold
          <SecretConfidenceInfoTooltip />
        </CardTitle>
        <CardDescription>
          Secret leakage alerts below the chosen confidence are hidden from your
          dashboard, won't notify you, and won't be blocked in Enforce mode.
          Secrets matched by the regex engine have no confidence score and are
          always shown.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {hasError ? (
          <LoadError setting="secret alert confidence threshold" />
        ) : (
          <SecretConfidenceThresholdControl
            value={confidence}
            onChange={onChange}
            disabled={isLoading}
            saving={isSaving}
          />
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Personal Policies → Policy Settings (KIR-5106). Each card mounts, and
 * fetches, only while its flag is on; selects stay locked until loaded.
 */
export function PersonalPolicySettingsPage({
  isEnforcePolicyModeEnabled,
  isSecretConfidenceThresholdEnabled,
}: Props): ReactElement {
  return (
    <div className="space-y-4">
      {isEnforcePolicyModeEnabled && <EnforcementCard />}
      {isSecretConfidenceThresholdEnabled && <ConfidenceCard />}
    </div>
  );
}
